import { Heading } from '@mm/components';
import { InfoIcon, PlusCircleIcon } from '@mm/icons';
import { useLeadAssignmentsCRUDActions } from '@mm/state/actions/LeadAssignmentsCRUDActions';
import { useLeadAssignmentGroupsCRUDActions } from '@mm/state/actions/LeadAssignmentsGroupCRUDActions';
import { LeadAssignment } from '@mm/state/interfaces/leadAssignments';
import CompanyUsersModel from '@mm/state/model/CompanyUsersModel';
import { selectLeadAssignmentGroups } from '@mm/state/selectors/leadAssignmentGroupsSelector';
import {
  activeChatbotIdSelector,
  activeCompanyUsers,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useActor } from '@xstate/react';
import React, { useEffect } from 'react';
import { Draggable, DropResult } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { useContextSelector } from 'use-context-selector';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { MMdndDroppable } from '../../../../../../common/components/MMdndDroppable/MMdndDroppable';
import { reorder } from '../../../../../../common/helpers/utils/sorting';
import {
  ContactContext,
  EmptySelectedContact,
} from '../../../../liveChat/contactMachine';
import { LiveChatContext } from '../../../../liveChat/LiveChatContext';
import { colors } from '../../../styles/colors';
import { AgentGroup } from './AgentGroup/AgentGroup';
import { LeadAssignmentItem } from './LeadAssignmentItem/LeadAssignmentItem';
import './LeadAssignments.scss';

type Props = {
  leadAssignments: LeadAssignment[];
  className?: string;
};

export const LeadAssignments = (props: Props) => {
  const { leadAssignments } = props;
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const leadAssignmentGroups = useSelector(selectLeadAssignmentGroups);
  const companyUsers = useSelector(activeCompanyUsers);

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineState] = useActor(lcService);

  const remoteFbPageRailsId = useSelector(
    (state: StoreState) => state.connectedPage.pageDetails?.id,
  );

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const { retrieveAll, create: createGroup } =
    useLeadAssignmentGroupsCRUDActions();
  const { create, update } = useLeadAssignmentsCRUDActions();

  useEffect(() => {
    retrieveAll(activeChatbotId);
  }, []);

  const selectedConversation = selectedContact?.conversation;
  const usersWithAccess = (user: CompanyUsersModel) => {
    if (
      !['facebook_page_collaborator', 'live_chat_agent'].find(
        r => r === user.roleCode,
      )
    ) {
      return true;
    }
    if (
      selectedConversation?.assignedUserId?.toString() === user.id.toString()
    ) {
      return true;
    }
    if (!remoteFbPageRailsId) {
      return true;
    }
    return (user.allowedFacebookPageIds || []).includes(remoteFbPageRailsId);
  };
  const agents = [...companyUsers.filter(usersWithAccess)];

  const onAddRule = () => {
    create(activeChatbotId, {
      sortOrder: leadAssignments.length + 1,
      name: 'Lead Assignment',
    });
  };

  const onAddAgent = () => {
    createGroup(activeChatbotId, {
      name: 'Agent Group',
    });
  };
  const onDragEnd = (result: DropResult) => {
    if (result.destination) {
      const leadAssignmentsFiltered = reorder(
        leadAssignments,
        result.source.index,
        result.destination.index,
      ).map((s, i) => ({ ...s, sortOrder: i }));
      leadAssignmentsFiltered.forEach(item => {
        update(activeChatbotId, item.id, {
          sortOrder: item.sortOrder,
        });
      });
    }
  };

  return (
    <div className="lead-assignment">
      <div className="lead-assignment__heading">Contact Assignments</div>
      <div className="lead-assignment__info-message">
        <InfoIcon className="lead-assignment__info-icon" />
        <span>
          Automatically assign contacts to available agents based on Assignment
          Rules
        </span>
      </div>
      <MMdndDroppable
        droppableId="droppable-lead-assignments"
        type="droppable-lead-assignments"
        onDragEnd={result => onDragEnd(result)}
      >
        {droppableProvided => (
          <div ref={droppableProvided.innerRef}>
            {leadAssignments.map((item, index) => {
              return (
                <Draggable
                  key={item.id}
                  draggableId={`${item.id}`}
                  index={index}
                >
                  {(draggableProvided, draggableStateSnapshot) => (
                    <div
                      key={item.id}
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                    >
                      <LeadAssignmentItem
                        record={item}
                        agents={agents}
                        groups={leadAssignmentGroups}
                        draggableProvided={draggableProvided}
                      />
                    </div>
                  )}
                </Draggable>
              );
            })}
          </div>
        )}
      </MMdndDroppable>
      <PrimaryButton
        className="lead-assignment__add-new-button"
        color={ButtonColor.secondaryBlue}
        onClick={onAddRule}
      >
        <PlusCircleIcon
          color={colors.accentBlue}
          className="lead-assignment__add-new-button-icon"
        />
        Add new Rule
      </PrimaryButton>
      <div className="lead-assignment__agents">
        <Heading
          size={5}
          text="Agents Groups"
          className="lead-assignment__agents-header"
        />
        <div className="lead-assignment__agents-groups">
          {leadAssignmentGroups.map(group => (
            <AgentGroup group={group} agents={agents} key={group.id} />
          ))}

          <PrimaryButton
            className="lead-assignment__add-new-button"
            color={ButtonColor.secondaryBlue}
            onClick={onAddAgent}
          >
            <PlusCircleIcon
              color={colors.accentBlue}
              className="lead-assignment__add-new-button-icon"
            />
            Add agent Group
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
